import { createContext, PropsWithChildren, ReactNode, useContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  createBrowserRouter,
  Navigate,
  Route,
  RouterProvider,
  useLocation,
  useNavigate
} from 'react-router-dom';
import { Routes } from '@datadog/browser-rum-react/react-router-v6';
import { Role } from '@blisspointmedia/bpm-types/dist/Users';
import { LegacyRmmmRedirect } from '@Pages/MMM/LegacyRmmmRedirect';
import { NotFound } from '@Pages/NotFound';
import { AmazonRedirect } from '@Pages/RetailOpsChannels/AmazonRedirect';
import { useAppSelector } from '../../redux';
import { SetCompany } from '../../redux/company';
import * as UserRedux from '../../redux/user';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useCompanyInfoFetch } from '../../utils/hooks/useCompanyInfoFetch';
import { getCompanySlug, useLocationCompany } from '../../utils/hooks/useLocationCompany';
import { NavLocationsProvider, useNavLocations } from '../../utils/routing/location-utils';
import { EmptyPage } from '../Page/EmptyPage';
import { RouteErrorFallback } from './RouteErrorFallback';
import { ProtectedPage } from '../ProtectedPage';

// Post-login landing for the root path. Admins (role <= 1, matching the /companies route's
// roleRequired) get the Companies list; everyone else lands on their first company's default page
// (cross-channel, matching bpm-frontend's historical default). Only rendered once
// BpmRouterInner's isReady gate has passed, so `companies` is guaranteed loaded here.
const RootRedirect = (): ReactNode => {
  const companies = useAppSelector(UserRedux.companiesSelector);
  const userRole = useAppSelector(UserRedux.roleSelector);
  if (userRole !== undefined && userRole <= 1) {
    return <Navigate to='/companies' replace />;
  }
  const firstCompany = companies[0]?.company;
  return <Navigate to={firstCompany ? `/${firstCompany}/cross-channel` : '/companies'} replace />;
};

// Route definitions stay in the existing tree; the outer data router enables navigation blockers.
const BpmRouterInner = ({ children }: PropsWithChildren): ReactNode => {
  useCompanyInfoFetch();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const company = useLocationCompany();
  const companyInfo = useCompanyInfo();
  const userEmail = useAppSelector(UserRedux.emailSelector);
  const companies = useAppSelector(UserRedux.companiesSelector);
  const { isReady, globalRoutes, clientRoutes } = useNavLocations();

  const isValidCompany = useMemo(
    () => companies.some(elem => elem.company === company),
    [companies, company]
  );

  // Extract only the first path segment so this only re-runs on company slug changes,
  // not on every sub-path navigation (e.g. tab switches within the same page).
  const rawCompanySlug = getCompanySlug(pathname);

  // If there is any capitalization in the URI's company slug, replace it.
  // Reads the raw URL segment rather than the value from useLocationCompany,
  // which returns the canonical company name and therefore is always lowercase.
  useEffect(() => {
    if (rawCompanySlug && rawCompanySlug !== rawCompanySlug.toLowerCase()) {
      navigate(pathname.replace(rawCompanySlug, rawCompanySlug.toLowerCase()), { replace: true });
    }
  }, [rawCompanySlug, navigate, pathname]);

  // Set redux current company to match what's in the URI
  useEffect(() => {
    if (company && isValidCompany && (!companyInfo.cid || company !== companyInfo.cid)) {
      dispatch(SetCompany(company));
    }
  }, [company, isValidCompany, companyInfo.cid, dispatch]);

  // Unset redux current company when URI does not have a company
  useEffect(() => {
    if ((!company || !isValidCompany) && company !== companyInfo.cid) {
      dispatch(SetCompany(''));
    }
  }, [company, isValidCompany, companyInfo.cid, dispatch]);

  if (Boolean(userEmail) && !isReady) return <EmptyPage />;

  return (
    <Routes>
      <Route element={children}>
        {/* Root sends the user to their post-login home: the Companies list for admins, or their
            first company's default page for everyone else. See RootRedirect. */}
        <Route index element={<RootRedirect />} />
        {globalRoutes}
        {/* RetailOpsChannels - Amazon SPAPI OAuth lands here after the user authorizes in Amazon's consent screen.
            Registered top-level (not under :company) because Amazon's app config only allows one
            static redirect_uri — the company that kicked off the flow is threaded through the
            `state` query param instead (see AmazonRedirect's decodeState) and read from there.
            Matches bpm-frontend's App.jsx, which registers /amzn_callback the same way. */}
        <Route
          path='amzn_callback'
          element={<ProtectedPage component={AmazonRedirect} roleRequired={Role.EXTERNAL} />}
        />
        <Route path=':company'>
          {/* Only mount the per-company page routes for a real company slug. Otherwise any
              subpath falls through to the `*` catch-all below instead of matching a page route
              under an invalid/mistyped company (e.g. /sfakjdsg/streaming/optimizations). */}
          {isValidCompany ? clientRoutes : null}
          <Route path='rmmm/*' element={<LegacyRmmmRedirect />} />
          <Route path='mmm/*' element={<LegacyRmmmRedirect />} />
          {/* Custom dashboard routes are registered per-company as literal paths (one per
              custom_dashboards entry — see buildCustomDashboardSubNavs in location-utils.tsx), so
              switching companies via ClientSelector (a plain string-replace of the company segment,
              staying on the same relative path) can land on a `custom/<slug>` path that doesn't
              exist for the new company — either it lacks that specific dashboard, or has no custom
              dashboards at all. A real dashboard for the current company still matches its own
              literal route first (more specific than this splat); only an unmatched one falls
              through here. Redirect to the bare company path rather than hardcoding a page — it
              defers to the `index` route below for the actual default-page decision. */}
          <Route path='custom/*' element={<Navigate to={`/${company}`} replace />} />
          {/* Bare `/{company}` (no page at all) — e.g. the Companies list's company links go
              straight here. Matches RootRedirect's default-page convention one level up: send
              the user to Cross-Channel rather than 404. Must be a separate route from the `*`
              catch-all below — combining `index` and `path='*'` on one <Route> makes react-router
              match on `path` (a `:company/*` splat, which matches the bare URL too) and ignore
              `index` for routing purposes, so the bare path silently rendered NotFound instead.
              Guard on isValidCompany: only redirect when the first segment is a real company.
              An unknown slug (a mistyped URL, or a deep-link whose page route never registered
              — e.g. a gated feature the user lacks) otherwise lands on Cross-Channel, which then
              fires getCustomMetrics with the now-unset company and throws an unhandled promise
              rejection. Fall those back to NotFound, matching the pre-redirect behavior. */}
          <Route
            index
            element={isValidCompany ? <Navigate to='cross-channel' replace /> : <NotFound />}
          />
          <Route path='*' element={<NotFound />} />
        </Route>
        <Route path='*' element={<NotFound />} />
      </Route>
    </Routes>
  );
};

const RouterChildren = createContext<ReactNode>(null);
const RouterRoot = () => {
  const children = useContext(RouterChildren);
  return (
    <NavLocationsProvider>
      <BpmRouterInner>{children}</BpmRouterInner>
    </NavLocationsProvider>
  );
};
const router = createBrowserRouter([
  { path: '*', element: <RouterRoot />, errorElement: <RouteErrorFallback /> }
]);

export const BpmRouter = ({ children }: PropsWithChildren): ReactNode => (
  <RouterChildren.Provider value={children}>
    <RouterProvider router={router} />
  </RouterChildren.Provider>
);
